import React from 'react';
import { Box, Typography, Grid, Card, CardContent, Container, Paper, Avatar, Divider } from '@mui/material';
import { useLanguage } from '../i18n/LanguageContext';
import { motion } from 'framer-motion';
import heroBg from "../imaged/3668b58e-26f4-4e05-9ecc-88ae22234d67.jpg";
import leafBg from "../imaged/leaf.jpg";
import contactImg from "../imaged/contact.jpg";
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import VisibilityIcon from '@mui/icons-material/Visibility';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import EmojiNatureIcon from '@mui/icons-material/EmojiNature';
import PsychologyIcon from '@mui/icons-material/Psychology';
import PersonIcon from '@mui/icons-material/Person';
import QuoteIcon from '@mui/icons-material/FormatQuote';

const MotionBox = motion(Box);
const MotionCard = motion(Card);

const AboutParitranam = () => {
  const { t } = useLanguage();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.6 } }
  };

  return (
    <Box sx={{ overflowX: 'hidden' }}>
      {/* Hero Section */}
      <Box 
        sx={{ 
          position: 'relative', 
          height: '85vh', 
          minHeight: 600,
          backgroundImage: `url("${heroBg}")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
          display: 'flex',
          alignItems: 'center',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'linear-gradient(to right, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.2) 100%)'
          }
        }}
      >
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
          <Grid container>
            <Grid item xs={12} md={9}>
              <MotionBox 
                initial={{ x: -50, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.8 }}
              >
                <Typography variant="overline" sx={{ color: '#81c784', letterSpacing: 4, fontSize: '1.1rem', fontWeight: 700, mb: 2, display: 'block' }}>
                  {t('aboutParitranam.overline')}
                </Typography>
                <Typography variant="h1" sx={{ color: '#fff', fontWeight: 800, mb: 3, fontSize: { xs: '3rem', md: '5.5rem' }, lineHeight: 1.1 }}>
                  {t('aboutParitranam.title')}
                </Typography>
                <Typography variant="h5" sx={{ color: 'rgba(255,255,255,0.95)', maxWidth: 800, lineHeight: 1.6, fontWeight: 300, fontSize: '1.4rem' }}>
                  {t('aboutParitranam.subtitle')}
                </Typography>
              </MotionBox>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ py: 10 }}>
        <MotionBox 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={containerVariants}
        >
          
          {/* Intro Section */}
          <Grid container spacing={8} sx={{ mb: 12 }}>
            <Grid item xs={12} md={6}>
              <MotionBox variants={itemVariants}>
                <Typography variant="h3" fontWeight={800} sx={{ mb: 4, color: 'text.primary' }}>
                  {t('aboutParitranam.storyTitle')}
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.9, color: 'text.secondary', whiteSpace: 'pre-line' }}>
                  {t('aboutParitranam.intro')}
                </Typography>
              </MotionBox>
            </Grid>
            <Grid item xs={12} md={6}>
              <MotionBox variants={itemVariants} sx={{ height: '100%' }}>
                <Box sx={{ position: 'relative', height: '100%', minHeight: 300 }}>
                  <Box 
                    sx={{ 
                      position: 'absolute',
                      top: 0, left: 0, right: 0, bottom: 0,
                      backgroundImage: `url("${leafBg}")`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      borderRadius: 6,
                      opacity: 0.8
                    }} 
                  />
                  <Card sx={{ 
                    position: 'relative', 
                    top: '50%', 
                    transform: 'translateY(-50%) translateX(-20px)',
                    ml: 4,
                    p: 4,
                    borderRadius: 4,
                    boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
                  }}>
                    <QuoteIcon sx={{ fontSize: 40, color: 'primary.light', transform: 'rotate(180deg)' }} />
                    <Typography variant="h5" sx={{ fontStyle: 'italic', my: 2, fontWeight: 500, color: 'text.primary' }}>
                      {t('aboutParitranam.meaningText')}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                      <EmojiNatureIcon sx={{ color: 'primary.main', mr: 1 }} />
                      <Typography variant="subtitle2" fontWeight={700} color="primary.main">
                        {t('aboutParitranam.meaningTitle')}
                      </Typography>
                    </Box>
                  </Card>
                </Box>
              </MotionBox>
            </Grid>
          </Grid>

          {/* Vision & Mission Cards */}
          <Box sx={{ mb: 12 }}>
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <MotionCard 
                  variants={itemVariants}
                  whileHover={{ y: -10 }}
                  sx={{ 
                    p: 5, 
                    borderRadius: 6, 
                    bgcolor: '#1b5e20', 
                    color: 'white',
                    height: '100%',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <VisibilityIcon sx={{ fontSize: 50, mb: 3, opacity: 0.8 }} />
                    <Typography variant="h4" fontWeight={700} sx={{ mb: 2 }}>
                      {t('aboutParitranam.visionTitle')}
                    </Typography>
                    <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.8, opacity: 0.9 }}>
                      {t('aboutParitranam.visionText')}
                    </Typography>
                  </Box>
                  <Box sx={{ 
                    position: 'absolute', 
                    right: -50, bottom: -50, 
                    width: 200, height: 200, 
                    bgcolor: 'rgba(255,255,255,0.05)', 
                    borderRadius: '50%' 
                  }} />
                </MotionCard>
              </Grid>
              <Grid item xs={12} md={6}>
                <MotionCard 
                  variants={itemVariants}
                  whileHover={{ y: -10 }}
                  sx={{ 
                    p: 5, 
                    borderRadius: 6, 
                    bgcolor: '#fff',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.08)',
                    height: '100%',
                    border: '1px solid rgba(0,0,0,0.05)'
                  }}
                >
                  <TrackChangesIcon sx={{ fontSize: 50, mb: 3, color: 'primary.main' }} />
                  <Typography variant="h4" fontWeight={700} sx={{ mb: 2, color: 'text.primary' }}>
                    {t('aboutParitranam.missionTitle')}
                  </Typography>
                  <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.8, color: 'text.secondary' }}>
                    {t('aboutParitranam.missionText')}
                  </Typography>
                </MotionCard>
              </Grid>
            </Grid>
          </Box>

          {/* Leadership Section */}
          <Box sx={{ mb: 12 }}>
            <Typography variant="h3" fontWeight={800} textAlign="center" sx={{ mb: 6 }}>
              {t('aboutParitranam.leadershipTitle')}
            </Typography>
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <MotionBox variants={itemVariants}>
                  <Card sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, borderRadius: 4, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                    <Box sx={{ width: { xs: '100%', sm: 120 }, bgcolor: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
                      <PersonIcon sx={{ fontSize: 60, color: 'white' }} />
                    </Box>
                    <CardContent sx={{ p: 4, flex: 1 }}>
                      <Typography variant="h5" fontWeight={700} gutterBottom>
                        {t('aboutParitranam.founderTitle')}
                      </Typography>
                      <Divider sx={{ mb: 2, width: 50, borderBottomWidth: 3, borderColor: 'primary.light' }} />
                      <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8, whiteSpace: 'pre-line' }}>
                        {t('aboutParitranam.founderText')}
                      </Typography>
                    </CardContent>
                  </Card>
                </MotionBox>
              </Grid>
              <Grid item xs={12} md={6}>
                <MotionBox variants={itemVariants}>
                  <Card sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, borderRadius: 4, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                    <Box sx={{ width: { xs: '100%', sm: 120 }, bgcolor: 'secondary.main', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
                      <PsychologyIcon sx={{ fontSize: 60, color: 'white' }} />
                    </Box>
                    <CardContent sx={{ p: 4, flex: 1 }}>
                      <Typography variant="h5" fontWeight={700} gutterBottom>
                        {t('aboutParitranam.qualityHeadTitle')}
                      </Typography>
                      <Divider sx={{ mb: 2, width: 50, borderBottomWidth: 3, borderColor: 'secondary.light' }} />
                      <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8, whiteSpace: 'pre-line' }}>
                        {t('aboutParitranam.qualityHeadText')}
                      </Typography>
                    </CardContent>
                  </Card>
                </MotionBox>
              </Grid>
            </Grid>
          </Box>

          {/* Quality Assurance Section with Background */}
          <Box sx={{ position: 'relative', borderRadius: 6, overflow: 'hidden' }}>
            <Box 
              sx={{ 
                position: 'absolute',
                top: 0, left: 0, right: 0, bottom: 0,
                backgroundImage: `url("${contactImg}")`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                filter: 'brightness(0.3)'
              }} 
            />
            <Box sx={{ position: 'relative', zIndex: 1, p: { xs: 4, md: 8 } }}>
              <Grid container spacing={6}>
                <Grid item xs={12}>
                  <MotionBox variants={itemVariants}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <VerifiedUserIcon sx={{ fontSize: 40, color: '#4caf50', mr: 2 }} />
                      <Typography variant="h4" fontWeight={700} color="white">
                        {t('aboutParitranam.qualityPolicyTitle')}
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)', lineHeight: 1.8, mb: 6, fontSize: '1.1rem', whiteSpace: 'pre-line' }}>
                      {t('aboutParitranam.qualityPolicyText')}
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <HealthAndSafetyIcon sx={{ fontSize: 40, color: '#4caf50', mr: 2 }} />
                      <Typography variant="h4" fontWeight={700} color="white">
                        {t('aboutParitranam.qcqaTitle')}
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)', lineHeight: 1.8, fontSize: '1.1rem', whiteSpace: 'pre-line' }}>
                      {t('aboutParitranam.qcqaText')}
                    </Typography>
                  </MotionBox>
                </Grid>
              </Grid>
            </Box>
          </Box>

        </MotionBox>
      </Container>
    </Box>
  );
};

export default AboutParitranam;